import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { GalleryDto, GalleryPublicAccessLevel, TenantDto } from '@nusign/picflow-backend-types';
import { SwitchComponent } from '../../../components/switch/switch.component';
import { getFormControl } from '../../../lib/get-form-control';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { GalleryService } from '../../../services/gallery.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { InputFieldComponent } from '../../../shared-extra/components/input-field/input-field.component';
import { UpgradeBadgeComponent } from '../../../shared-extra/components/pro-tag/upgrade-badge.component';
import { CopyClipboardDirective } from '../../../shared-extra/directives/copy-to-clipboard';
import { InputPostDirective } from '../../../shared-extra/directives/input-post.directive';
import { BillingScopePipe } from '../../../shared-extra/pipes/billing-scope.pipe';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { AccessItemCtaComponent } from '../access-item-cta/access-item-cta.component';
import { AccessItemHeaderComponent } from '../access-item-header/access-item-header.component';
import { AccessItemComponent } from '../access-item/access-item.component';

@Component({
  selector: 'app-public-password-item',
  imports: [
    AccessItemComponent,
    AccessItemHeaderComponent,
    AccessItemCtaComponent,
    SwitchComponent,
    FormsModule,
    ReactiveFormsModule,
    UpgradeBadgeComponent,
    BillingScopePipe,
    InputFieldComponent,
    ButtonDirective,
    SvgComponent,
    CopyClipboardDirective,
    InputPostDirective,
    TooltipDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './public-password-item.component.html',
  styleUrl: './public-password-item.component.scss',
})
export class PublicPasswordItemComponent {
  private galleryService = inject(GalleryService);
  private state = inject(StateService);
  private toasts = inject(ToastService);
  private analytics = inject(PICFLOW_ANALYTICS);
  gallery = input.required<GalleryDto>();
  galleryPermissions = input.required<GalleryDto['permissions']>();
  editLink = output<void>();
  publicAccessLevels: GalleryPublicAccessLevel[] = ['review', 'view_only', 'no_access'];
  showPassword = model(false);
  editingPassword = signal(false);
  passwordForm = new FormGroup({
    password: getFormControl('gallery-password'),
  });

  isTeamMember = computed(() => {
    const gallery = this.gallery();
    if (!gallery) return;
    const tenantId = (gallery.tenant as TenantDto)?.id || (gallery.tenant as string);
    return this.state.tenants?.some(t => t.id === tenantId);
  });

  icon = computed(() => {
    const showPassword = this.showPassword();
    if (showPassword) return '/assets/icons/lock.svg';
    return '/assets/icons/lock-off.svg';
  });

  iconColor = computed(() => {
    const showPassword = this.showPassword();
    if (showPassword) return 'orange';
    return 'grey';
  });

  constructor() {
    effect(() => {
      const gallery = this.gallery();
      if (!gallery) return;
      if (this.editingPassword()) return;
      this.showPassword.set(gallery.password_enabled);
      this.passwordForm.patchValue({ password: gallery.password });
    });
  }

  toggleGalleryPassword() {
    if (this.showPassword() && !this.gallery().password) {
      this.editingPassword.set(true);
      return; // if we don't have a password, we can't enable it
    }
    this.editingPassword.set(false);
    if (!this.showPassword() && !this.gallery().password_enabled) return; // If the password is not enabled, we can't disable it

    this.galleryService.updateGallery(this.gallery(), {
      password_enabled: this.showPassword(),
      password: this.showPassword() ? this.gallery().password : undefined,
    });
  }

  savePassword() {
    if (!this.passwordForm.valid) return;
    this.editingPassword.set(false);
    this.galleryService.updateGallery(this.gallery(), {
      password: this.passwordForm.value.password,
      password_enabled: true,
    });
  }

  onPasswordCopied() {
    this.toasts.add('Password Copied', 'success');
    this.analytics.track('gallery_password_copied');
  }
}
